import React, { useState } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Form, Input, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { Modal, FormLabel, Button } from '../../../components';

export const RESET_PASSWORD_QUERY = gql`
    mutation resetPassword($input: ResetPasswordInput!) {
        resetPassword(input: $input) {
            success
        }
    }
`;

const SubmitButton = styled(Button)`
    margin: 0px !important;
    padding-top: 1em !important;
    padding-bottom: 1em !important;
    text-transform: uppercase !important;
`;

const ModalActions = styled(Modal.Actions)`
    padding-left: 1.5em !important;
    padding-right: 1.5em !important;
`;

const LoginLink = styled.div`
    margin-top: 14px;
    text-align: center;
`;

const StyledSuccessMessage = styled(Message)`
    text-align: center;
`;

const intlMessages = defineMessages({
    emailRequired: {
        id: 'pages.login.resetPassword.emailRequired',
        defaultMessage: 'Enter your email address',
    },
    emailNotExists: {
        id: 'pages.login.resetPassword.emailNotExists',
        defaultMessage: 'User with specified email address does not exists.',
    },
    defaultErrorMessage: {
        id: 'pages.login.resetPassword.defaultErrorMessage',
        defaultMessage: 'Unable to reset your password.',
    },
    emailLabel: {
        id: 'pages.login.resetPassword.emailLabel',
        defaultMessage: 'E-mail address',
    },
    emailPlaceholder: {
        id: 'pages.login.resetPassword.emailPlaceholder',
        defaultMessage: 'Please enter your email address',
    },
    successMessage: {
        id: 'pages.login.resetPassword.successMessage',
        defaultMessage: 'Please check your email, and follow instructions.',
    },
    submit: {
        id: 'pages.login.resetPassword.submit',
        defaultMessage: 'Reset password',
    },
    loginLink: {
        id: 'pages.login.resetPassword.loginLink',
        defaultMessage: 'Already have account?',
    },
});

function ResetPasswordForm({ showResetPasswordForm, intl }) {
    const [success, setSuccess] = useState(false);
    const [resetPassword] = useMutation(RESET_PASSWORD_QUERY);
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: { email: '' } });

    const onSubmit = async ({ email }) => {
        try {
            const { data } = await resetPassword({ variables: { input: { email } } });
            setSuccess(data.resetPassword.success);
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError && graphQLError.name === 'NotExists') {
                setError('email', {
                    message: intl.formatMessage(intlMessages.emailNotExists),
                });
                return;
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    if (success) {
        return (
            <StyledSuccessMessage success>
                <FormattedMessage {...intlMessages.successMessage} />
            </StyledSuccessMessage>
        );
    }

    return (
        <Modal.Context>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.email}>
                        <FormLabel>
                            <FormattedMessage {...intlMessages.emailLabel} />
                        </FormLabel>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: intl.formatMessage(intlMessages.emailRequired),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    placeholder={intl.formatMessage(
                                        intlMessages.emailPlaceholder,
                                    )}
                                    type="text"
                                    size="big"
                                    fluid
                                />
                            )}
                        />
                        {errors.email && (
                            <BaseLabel color="red" pointing basic>
                                {errors.email.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <ModalActions>
                <SubmitButton
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    primary
                    fluid
                >
                    <FormattedMessage {...intlMessages.submit} />
                </SubmitButton>
                <LoginLink>
                    <a href="#login" onClick={() => showResetPasswordForm(false)}>
                        <FormattedMessage {...intlMessages.loginLink} />
                    </a>
                </LoginLink>
            </ModalActions>
        </Modal.Context>
    );
}

export default injectIntl(ResetPasswordForm);
